import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import UserAvatar from "@/components/UserAvatar";
import { getInitials } from "@/lib/avatar";
import { toast } from "sonner";
import { Check, X, Clock, ShieldCheck } from "@phosphor-icons/react";

/**
 * PendingApprovalsDialog — shared UI for admin/directivo to approve/reject
 * enrollment requests from players on Tournaments / Leagues / Retas.
 *
 * Props:
 *   open, onClose
 *   kind:  'tournament' | 'league' | 'reta'
 *   eventId: uuid
 *   eventName: string
 *   onChanged?: () => void — called after each approve/reject so parent can refresh
 */
export default function PendingApprovalsDialog({ open, onClose, kind, eventId, eventName, onChanged }) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const base = kind === "tournament"
    ? `/tournaments/${eventId}`
    : kind === "league"
    ? `/leagues/${eventId}`
    : `/retas/multi/${eventId}`;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${base}/pending`);
      setPending(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar solicitudes");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open && eventId) load(); /* eslint-disable-next-line */ }, [open, eventId]);

  const act = async (uid, action) => {
    setBusy((b) => ({ ...b, [uid]: action }));
    try {
      await api.post(`${base}/${action}/${uid}`);
      toast.success(action === "approve" ? "Inscripción aprobada" : "Solicitud rechazada");
      setPending((p) => p.filter((u) => u.id !== uid));
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setBusy((b) => { const { [uid]: _, ...rest } = b; return rest; });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pending-approvals-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Barlow_Condensed'] text-2xl font-black uppercase tracking-tighter flex items-center gap-2">
            <ShieldCheck size={22} weight="fill" className="text-[#CCFF00]" />
            Solicitudes pendientes
          </DialogTitle>
          <p className="text-xs text-gray-500">{eventName}</p>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center"><div className="inline-block w-6 h-6 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>
        ) : pending.length === 0 ? (
          <div className="py-8 text-center" data-testid="pending-empty">
            <Clock size={32} className="mx-auto text-gray-600 mb-2" />
            <p className="text-sm text-gray-400">Sin solicitudes pendientes.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="pending-list">
            {pending.map((u) => (
              <div key={u.id} className="p-3 bg-black/40 border border-white/10 rounded flex items-center gap-3" data-testid={`pending-user-${u.id}`}>
                <UserAvatar
                  src={u.profile_photo_url || u.photo}
                  fallback={getInitials(u.name || "?")}
                  className="w-12 h-12 rounded-full border border-white/10"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-semibold truncate">{u.name}</p>
                  <div className="flex items-center gap-2 flex-wrap text-[10px] text-gray-500 mt-0.5">
                    {u.category && <Badge variant="outline" className="text-[9px] uppercase h-4 border-white/15 text-gray-400">Cat {u.category}</Badge>}
                    {u.email && <span className="truncate max-w-[180px]">{u.email}</span>}
                    {u.phone && <span>{u.phone}</span>}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    size="sm"
                    onClick={() => act(u.id, "approve")}
                    disabled={!!busy[u.id]}
                    className="bg-[#CCFF00] text-black hover:bg-[#B3E600] h-8 px-3 text-[10px] uppercase font-bold"
                    data-testid={`pending-approve-${u.id}`}
                  >
                    <Check size={12} className="mr-1" />
                    {busy[u.id] === "approve" ? "…" : "Aprobar"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => act(u.id, "reject")}
                    disabled={!!busy[u.id]}
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8 px-3 text-[10px] uppercase font-bold"
                    data-testid={`pending-reject-${u.id}`}
                  >
                    <X size={12} className="mr-1" />
                    {busy[u.id] === "reject" ? "…" : "Rechazar"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
